import { useBaseViewModel } from '@/common';
import { EmbedReportUseCase } from '@/domain/use-cases/embed-report';
import { NoPermissionView } from '@/modules/403/NoPermissionView';
import { ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { Button, Spin } from 'antd';
import { get } from 'lodash';
import { useEffect, useState } from 'react';
import { EmbedReportRepoImpl } from '../adapters/repositories';
import { useZoom } from '../hooks/useZoom';
import { ReportType, useTrackReportViewed } from '@/core/analytics';
// import { config } from '@/config';

interface Props {
  reportData: any;
}

const ReportItemData = (props: Props) => {
  const reportConfig = new EmbedReportUseCase(new EmbedReportRepoImpl());
  const { reportData } = props;
  const { loading, error, catchAction, setError } = useBaseViewModel();
  const [metabaseUrl, setMetabaseUrl] = useState('');
  const [reportId, setReportId] = useState('');
  const [reportType, setReportType] = useState<{ id: string; type: ReportType }>();

  // PowerBI logic
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const pbi = require('powerbi-client');
  const mainReportStorageKey = 'MAIN_REPORT';
  const { zoomRate, zoomIn, zoomOut } = useZoom(300, 50, mainReportStorageKey);
  const getItem = (item: any) => {
    catchAction(async () => {
      const { data } = await reportConfig.run(item.id);
      // for metabase report
      const reportUrl = get(data, 'embedUrl');
      if (reportUrl.includes('metabase')) {
        setReportType({ id: item.id, type: 'metabase' });
        setMetabaseUrl(reportUrl);
        return;
      }
      setReportType({ id: item.id, type: 'powerbi' });

      const embedConfigs = await {
        type: 'report',
        id: item.reportId,
        group: item.groupId,
        embedUrl: reportUrl,
        tokenType: pbi.models.TokenType.Aad,
        accessToken: get(data, 'embedToken'),
      };

      const powerbi = new pbi.service.Service(
        pbi.factories.hpmFactory,
        pbi.factories.wpmpFactory,
        pbi.factories.routerFactory,
      );
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      powerbi.embedNew(document.getElementById(item.id)!, embedConfigs);
    });
  };

  useTrackReportViewed(
    reportType && reportType.id === reportData?.id
      ? {
          id: reportData.id,
          name: reportData.name,
          url: reportData.url,
          type: reportType.type,
        }
      : undefined,
  );

  useEffect(() => {
    if (reportData.id) {
      setReportId(reportData.id);
    }
    setError(undefined);
  }, [reportData]);

  useEffect(() => {
    if (reportId && reportData) {
      console.log('reportData', reportData);
      getItem(reportData);
    }
  }, [reportId]);

  return (
    <>
      {metabaseUrl && (
        <div
          className='metabase-container'
          style={{
            width: '100%',
            height: 'calc(100vh - 168px)', // Account for header (64px) + margins (24px*2) + footer (~56px)
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: '#f5f5f5',
          }}
        >
          <iframe
            src={metabaseUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              position: 'relative', // Override global absolute positioning
              top: 'unset',
              left: 'unset',
            }}
          />
        </div>
      )}
      {!metabaseUrl && (
        <div
          style={{
            height: 'calc(100vh - 95px)',
            position: 'relative',
            overflow: 'auto',
          }}
        >
          <div>
            {loading && (
              <div
                style={{
                  height: '500px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Spin />
              </div>
            )}
          </div>
          {error && <NoPermissionView />}
          {!error && (
            <div
              id={reportData.id}
              style={{
                transition: ' all 0.5s',
                height: '100%',
                position: 'relative',
                overflow: 'auto',
                width: `${(100 * zoomRate) / 100}%`,
                paddingTop: `${(50 * zoomRate) / 100}%`,
              }}
              // eslint-disable-next-line react/no-unknown-property
              powerbi-settings-filter-pane-enabled='false'
            />
          )}
          <div className='zoom-controller'>
            <div style={{ fontSize: '0.75rem' }}>{zoomRate}%</div>
            <Button
              icon={<ZoomInOutlined />}
              size='small'
              style={{ margin: '0.25rem 0' }}
              onClick={zoomIn}
              disabled={zoomRate >= 300}
            />
            <Button
              icon={<ZoomOutOutlined />}
              size='small'
              onClick={zoomOut}
              disabled={zoomRate <= 50}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ReportItemData;
